import {Schema} from "yup";
import * as yup from "yup";
import pluralize from "pluralize";
import {ApiQuestionRenderedModel} from "../../services/api/questions/ApiQuestionModel";
import {s2positivei} from "../utils";
import {SkSelectValuesFromApiVariants} from "../../components/SkForms/SkForm";

const REACT_APP_SELECT_AUTOSUGGEST_FROM_ITEMS_GTE = s2positivei(process.env.REACT_APP_SELECT_AUTOSUGGEST_FROM_ITEMS_GTE, 5);

export type MandatoryMode = "all" | "noone" | "auto";

const getNonLatinString = () => yup.string().test("is-cyrillic", "Please use only latin letters", (value) => (`${value}`.match(/[А-яё]/gm)) === null);
const getLatinAndNumbersString = () => yup.string().test("is-latin", "Please use only cyrillic letters", (value) => {
  if (!value || value.trim() === '') return true; // Для необязательных полей
  return /^[А-яё0-9№\s().,:;\/-]*$/.test(value as any);
});
const getNumberAndSpacesString = () => yup.string().matches(/^$|^[\d -]*$/, 'Please use only digits, "-" and spaces')
// Паспортные серия/номер: международные документы содержат латинские буквы, поэтому
// digits-only (docnumber) сюда не подходит — разрешаем латиницу, цифры, пробел, "-", "#", "№".
const getPassportNumberString = () => yup.string().matches(/^$|^[A-Za-z0-9№# -]*$/, 'Please use only latin letters, digits, "#", "№", "-" and spaces')

const ValidationSchemaFromQuestions = (questions:ApiQuestionRenderedModel[], mandatoryMode: MandatoryMode = "auto") => {
  const schemaShape: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [questionId: string]: Schema<any>;
  } = {};

  questions.forEach(q => {
    const mandatory = mandatoryMode === "all" ? true : mandatoryMode === "noone" ? false : q.answers_count_min > 0;
    const enforceRequired = mandatoryMode !== "noone";
    let questionRule;

    if (q.display_type === "selectfree" || q.display_type === "select") {
      if (q.display_type === "selectfree" && q.group === "military") {
        questionRule = yup.string();
      } else {
        questionRule = getNonLatinString();
      }
      
      if (enforceRequired && (q.tag === "educational_level" || q.tag === "educational_program" || q.tag === "gender" || q.tag === "hear_source" || q.tag === "research_proposal")) {
        questionRule = questionRule.required(`${q.name} is required.`);
      }
      if (q.display_type === "select" && !q.disable_autocomplete && q.variants.length > REACT_APP_SELECT_AUTOSUGGEST_FROM_ITEMS_GTE) {
        const variantKeys = Object.keys(SkSelectValuesFromApiVariants(q));
        // Необязательный автосаджест (answers_count_min=0) должен пропускать пустое значение,
        // но непустое — обязано быть из списка. Обязательные остаются строгими.
        // Без "" в oneOf yup 0.31 отвергает пустую строку даже у необязательного поля → Save заблокирован.
        questionRule = questionRule.oneOf(
          mandatory ? variantKeys : [...variantKeys, ""],
          "Incorrect value. Please, pick one of the suggested values."
        );
        if (mandatory) {
          questionRule = questionRule.required(`${q.name} is required.`);
        }
      }
      if (mandatory && q.dependency_resolution !== "visibility") {
        questionRule = questionRule.required(`${q.name} is required.`);
      }
    } else if (q.display_type === "email") {
      questionRule = getNonLatinString().email("Please enter correct email");
      if (mandatory) {
        questionRule = questionRule.required(`${q.name} is required`);
      }
    } else if (q.display_type === "number") {
      questionRule = yup.string().matches(/^[\d,]+$/, "Please enter correct number");
      if (mandatory) {
        questionRule = questionRule.required(`${q.name} is required`);
      }
    } else if (q.display_type === "out_of") {
      questionRule = yup.string()
        .matches(/^[\d,]+\/[\d,]/, "Please enter both correct numbers")
        .test("out-of-is-not-too-big", "Average can not be more than maximum", (value) => (parseFloat(`${value}`.split("/")[0].replace(",", ".")) <= parseFloat(`${value}`.split("/")[1].replace(",", "."))));
      if (mandatory) {
        questionRule = questionRule.required(`${q.name} is required`);
      }
    } else if (q.display_type === "upload") {
      questionRule = yup.array();
      if (enforceRequired) {
        questionRule = questionRule.min(q.answers_count_min, `You need to attach at least ${q.answers_count_min} ${pluralize("file", q.answers_count_min)}`);
      }

      if (q.answers_count_max > 0) {
        questionRule = questionRule.max(q.answers_count_max, `You cannot attach more than ${q.answers_count_max} ${pluralize("file", q.answers_count_max)}`);
      }
    } else if (q.display_type === "checkbox") {
      questionRule = yup.string();
      if (mandatory) {
        questionRule = questionRule.required(`${q.name.replace(/<[^>]*>/g, '')} is required`);
      }
    } else if(q.tag === "name" || q.tag === "surname" || q.tag === "country_of_citizenship" || q.tag === "country_of_living" || q.tag === "city_of_living" || q.tag === "research_proposal") {
      questionRule = yup.string().matches(/^[A-Za-z]+ig/, `Please enter both correct ${q.name}`);
      questionRule = getNonLatinString();
      if (enforceRequired) {
        questionRule = questionRule.required(`${q.name} is required`);
      }
    }
    else {
      if (q.tag === 'id-residence-address') {
        // Address of Residence заполняется на английском (зеркалит backend validate_latin).
        questionRule = getNonLatinString()
      } else if (q.tag === 'international-passport-number') {
        // "ID Document Series and Number" имеет group=docnumber, но загранпаспорта содержат буквы —
        // tag-проверка должна сработать раньше docnumber-правила (digits-only).
        questionRule = getPassportNumberString()
      } else if (q.tag === 'russian-passport-number') {
        // v8: серия и номер паспорта РФ — маска «XXXX XXXXXX» (4 цифры, пробел, 6 цифр).
        questionRule = yup.string().matches(/^$|^\d{4} \d{6}$/, 'Please enter the series and number as 4 digits, a space, then 6 digits (e.g. 1234 567890)')
      } else if (q.tag === 'russian-passport-subdivision-code') {
        // v8: код подразделения паспорта РФ — маска «XXX-XXX».
        questionRule = yup.string().matches(/^$|^\d{3}-\d{3}$/, 'Please enter the issuing authority code as XXX-XXX (e.g. 770-001)')
      } else if (q.group === 'military') {
        questionRule = getLatinAndNumbersString()
      } else if (q.group === 'address') {
        questionRule = yup.string()
      } else if (q.group === 'docnumber') {
          questionRule = getNumberAndSpacesString()      
      } else {
        questionRule = getNonLatinString();
      }
      if (mandatory) {
        questionRule = questionRule.required(`${q.name} is required`);
      }
    }

    schemaShape[q.id] = questionRule;
  });
  console.group("🔧 ValidationSchemaFromQuestions: Schema created");
  console.log("Questions processed:", questions.length);
  console.log("Mandatory mode:", mandatoryMode);
  
  // Log each question's validation rules
  questions.forEach(q => {
    const mandatory = mandatoryMode === "all" ? true : mandatoryMode === "noone" ? false : q.answers_count_min > 0;
    console.group(`Question: "${q.name}" (${q.id})`);
    console.log("Display type:", q.display_type);
    console.log("Tag:", q.tag);
    console.log("Group:", q.group);
    console.log("Is mandatory:", mandatory);
    console.log("Dependency resolution:", q.dependency_resolution);
    console.log("Answers count min:", q.answers_count_min);
    console.log("Answers count max:", q.answers_count_max);
    console.groupEnd();
  });
  
  console.log("Final schema shape keys:", Object.keys(schemaShape));
  console.groupEnd();

  return yup.object().shape(schemaShape);
};

export default ValidationSchemaFromQuestions;
